import { useMemo } from "react";
import {
  AssessmentScoringResult,
  LifeCyclePosition,
} from "types/assessmentTypes";
import {
  groupedIndustries,
  industriesMinMax,
  pnsCriteriasWieght,
} from "variables/lifeCycleDatas";

export const NEGATIVE_KPIS = [
  "CCC",
  "EMPLOYEE_TURNOVER",
  "LOGISTICS_TO_SALES",
  "ORGANIZATION_SIZE",
];

export const isCriteriaNegative = (criteria: {
  kpis: { code: string }[];
}): boolean => {
  return criteria?.kpis.some((kpi) => NEGATIVE_KPIS.includes(kpi.code));
};

export const getDisplayPercent = (criteria: {
  kpis: { code: string }[];
  correctedPercent: number;
}): number => {
  const originalPercent = criteria?.correctedPercent;

  if (isCriteriaNegative(criteria)) {
    return Math.max(0, Math.min(100, 100 - originalPercent));
  }

  return Math.max(0, Math.min(100, originalPercent));
};

// lifecycle
interface CriterionResult {
  index: number;
  correctedPercent: number;
  min: number;
  max: number;
  normalize: number;
  weight: number;
  filnalCriteriaScore: number;
  type: string;
}

interface IndustryLifeCycleResult {
  totalScore: number;
  stageKey: LifeCyclePosition;
  criteriaResults: CriterionResult[];
  industrySector: string;
  groupKey: string;
}

export function calculateIndustryLifeCycle(
  scoringResults: AssessmentScoringResult[],
  industrySectorKey: string
): IndustryLifeCycleResult | null {
  const firstResult = scoringResults?.[0];
  if (!firstResult) {
    console.error("No scoring results found");
    return null;
  }

  const criteriaBreakdown = firstResult.criteriaBreakdown;
  if (!criteriaBreakdown || criteriaBreakdown.length === 0) {
    console.error("No criteria breakdown found");
    return null;
  }

  const targetIndices = [1, 2, 3, 25, 28, 26, 27];

  const industryMinMax = industriesMinMax[industrySectorKey];
  if (!industryMinMax) {
    console.error(
      `Industry ${industrySectorKey} not found in industriesMinMax`
    );
    return null;
  }

  const findGroup = groupedIndustries.find((i) =>
    i.industries.includes(industrySectorKey)
  );
  const groupIntervals = findGroup.intervals;

  const criteriaResults: CriterionResult[] = [];

  for (const index of targetIndices) {
    const criterion = criteriaBreakdown.find((c) => c.criteriaIndex === index);

    if (!criterion) {
      console.warn(`Criterion ${index} not found`);
      continue;
    }

    const correctedPercent = getDisplayPercent(criterion) || 0;

    const range = industryMinMax[index];
    if (!range) {
      console.warn(`Range for index ${index} not found in industryMinMax`);
      continue;
    }

    const { min, max } = range;

    const weightInfo =
      pnsCriteriasWieght[index as keyof typeof pnsCriteriasWieght];
    if (!weightInfo) {
      console.warn(`Weight for index ${index} not found`);
      continue;
    }

    const { type, weight } = weightInfo;

    let normalize: number;
    if (type === "positive") {
      if (max === min) {
        normalize = 0;
      } else {
        normalize = (correctedPercent - min) / (max - min);
      }
    } else {
      if (min === max) {
        normalize = 0;
      } else {
        normalize = (min - correctedPercent) / (min - max);
      }
    }

    normalize = Math.max(0, Math.min(100, normalize));

    const filnalCriteriaScore = Math.min(
      weight,
      Math.max(0, normalize * weight)
    );

    criteriaResults.push({
      index,
      correctedPercent,
      min,
      max,
      normalize,
      weight,
      filnalCriteriaScore,
      type,
    });
  }

  const totalScore = criteriaResults.reduce(
    (sum, item) => sum + item.filnalCriteriaScore,
    0
  );

  const stage = groupIntervals.find((g) => {
    return g.min <= totalScore && totalScore <= g.max;
  });

  return {
    totalScore,
    stageKey: stage.title,
    criteriaResults,
    industrySector: industrySectorKey,
    groupKey: industrySectorKey,
  };
}

export function useIndustryLifeCycle(
  scoringResults: AssessmentScoringResult[],
  industrySectorKey: string
) {
  const result = useMemo(() => {
    if (!scoringResults || scoringResults.length === 0 || !industrySectorKey) {
      return null;
    }
    return calculateIndustryLifeCycle(scoringResults, industrySectorKey);
  }, [scoringResults, industrySectorKey]);

  return result;
}
